/**
 * AudioCode.js
 * Utilities for voice recording and audio processing
 */

// Constants for audio recording
const SILENCE_THRESHOLD = 0.01; // RMS threshold to detect speech
const SILENCE_DURATION_MS = 700; // Stop recording after 700ms of silence
const MAX_RECORDING_DURATION_MS = 30000; // Maximum 30 seconds
const SPEECH_CONFIRM_FRAMES = 3; // Number of frames to confirm speech

/**
 * Start recording audio from microphone
 * @param {Function} onAudioProcess - Callback for audio processing (for visualization)
 * @param {Function} onSpeechDetected - Callback when speech is detected
 * @param {Function} getIsMuted - Function to check if mic is muted (returns boolean)
 * @param {Function} onRecordingStopped - Callback when recording stops automatically (silence/timeout)
 * @returns {Promise<{audioContext: AudioContext, mediaStream: MediaStream, processor: ScriptProcessorNode, stopRecording: Function}>}
 */
export const startVoiceRecording = async (onAudioProcess = null, onSpeechDetected = null, getIsMuted = null, onRecordingStopped = null) => {
  try {
    // Request microphone access
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    // Create audio context
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const source = audioContext.createMediaStreamSource(stream);
    const processor = audioContext.createScriptProcessor(4096, 1, 1);

    const recordedBuffers = [];
    let isSpeaking = false;
    let loudFrameCount = 0;
    let silenceTimer = null;
    let recordingStartTime = Date.now();
    let isRecording = true;
    let recordingResult = null;

    // Function to reset silence timer
    const resetSilenceTimer = () => {
      if (silenceTimer) {
        clearTimeout(silenceTimer);
      }
      // Don't set silence timer if muted - we shouldn't auto-stop when muted
      const isMuted = getIsMuted ? getIsMuted() : false;
      if (isMuted) return;
      
      silenceTimer = setTimeout(() => {
        // Double-check not muted before stopping
        const stillMuted = getIsMuted ? getIsMuted() : false;
        if (isRecording && isSpeaking && !stillMuted) {
          const result = stopRecording("silence");
          // Trigger callback for automatic stop only if not muted
          if (onRecordingStopped && result && result.buffers && result.buffers.length > 0) {
            onRecordingStopped(result);
          }
        }
      }, SILENCE_DURATION_MS);
    };

    // Function to stop recording
    const stopRecording = (reason = "manual") => {
      if (!isRecording) return recordingResult;
      
      isRecording = false;
      
      if (silenceTimer) {
        clearTimeout(silenceTimer);
      }

      // Create result object before cleanup
      recordingResult = { 
        reason, 
        buffers: [...recordedBuffers], // Copy the array
        audioContext: {
          sampleRate: audioContext.sampleRate,
        }
      };

      // Stop media stream tracks
      stream.getTracks().forEach((track) => track.stop());

      // Disconnect audio nodes
      try {
        processor.disconnect();
        source.disconnect();
      } catch (e) {
        // Ignore disconnect errors
      }

      // Close audio context
      audioContext.close().catch(() => {});

      return recordingResult;
    };

    // Process audio chunks
    processor.onaudioprocess = (e) => {
      if (!isRecording) return;

      // Check if muted - skip recording buffers but still process for visualization
      const isMuted = getIsMuted ? getIsMuted() : false;

      const channelData = e.inputBuffer.getChannelData(0);
      
      // Only record buffers if not muted
      if (!isMuted) {
        recordedBuffers.push(new Float32Array(channelData));
      }

      // Calculate RMS (Root Mean Square) for volume detection
      let sum = 0;
      for (let i = 0; i < channelData.length; i++) {
        sum += channelData[i] * channelData[i];
      }
      const rms = Math.sqrt(sum / channelData.length);

      // Check for maximum recording duration (only if not muted)
      if (!isMuted) {
        const now = Date.now();
        if (now - recordingStartTime > MAX_RECORDING_DURATION_MS) {
          const result = stopRecording("timeout");
          // Trigger callback for automatic stop only if we have recorded buffers
          if (onRecordingStopped && result && result.buffers && result.buffers.length > 0) {
            onRecordingStopped(result);
          }
          return;
        }
      }

      // Detect speech (only if not muted)
      if (!isMuted && rms > SILENCE_THRESHOLD) {
        loudFrameCount++;
        if (loudFrameCount >= SPEECH_CONFIRM_FRAMES && !isSpeaking) {
          isSpeaking = true;
          if (onSpeechDetected) {
            onSpeechDetected();
          }
        }
        if (isSpeaking) {
          // Clear silence timer while speaking (we don't want to auto-stop during speech)
          if (silenceTimer) {
            clearTimeout(silenceTimer);
            silenceTimer = null;
          }
        }
      } else {
        // Silence detected
        loudFrameCount = 0;
        // If user was speaking and now stopped, start 2-second silence timer
        if (!isMuted && isSpeaking) {
          // Start timer when silence begins (only if not already running)
          if (!silenceTimer) {
            resetSilenceTimer();
          }
        }
        // If muted and was speaking, clear speaking state
        if (isMuted && isSpeaking) {
          isSpeaking = false;
          // Clear silence timer when muted
          if (silenceTimer) {
            clearTimeout(silenceTimer);
            silenceTimer = null;
          }
        }
      }

      // Call audio process callback for visualization (always, even when muted)
      if (onAudioProcess) {
        onAudioProcess(rms, channelData);
      }
    };

    source.connect(processor);
    processor.connect(audioContext.destination);

    return {
      audioContext,
      mediaStream: stream,
      processor,
      stopRecording: () => stopRecording("manual"),
      isRecording: () => isRecording,
    };
  } catch (error) {
    console.error("Error starting voice recording:", error);
    throw new Error("Failed to access microphone. Please check permissions.");
  }
};

/**
 * Convert recorded audio buffers to WAV blob
 * @param {Array<Float32Array>} buffers - Array of audio buffers
 * @param {number} sampleRate - Audio sample rate
 * @returns {Blob} WAV audio blob
 */
export const convertBuffersToWAV = (buffers, sampleRate = 44100) => {
  const length = buffers.reduce((sum, buffer) => sum + buffer.length, 0);
  const arrayBuffer = new ArrayBuffer(44 + length * 2);
  const view = new DataView(arrayBuffer);
  const samples = new Int16Array(arrayBuffer, 44);

  // WAV header
  const writeString = (offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  writeString(0, "RIFF");
  view.setUint32(4, 36 + length * 2, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(36, "data");
  view.setUint32(40, length * 2, true);

  // Convert samples
  let offset = 0;
  for (let i = 0; i < buffers.length; i++) {
    const buffer = buffers[i];
    for (let j = 0; j < buffer.length; j++) {
      const s = Math.max(-1, Math.min(1, buffer[j]));
      samples[offset++] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
  }

  return new Blob([arrayBuffer], { type: "audio/wav" });
};
